// cspell:ignore UNREVIEWED

export enum AppRole {
  Member = "Member",
  Admin = "Admin",
}

export enum GlobalTagIndexSource {
  TAGGER = "TAGGER",
  CUSTOM = "CUSTOM",
}

const createStringEnum = <const TValues extends readonly string[]>(
  values: TValues,
) =>
  Object.freeze(
    Object.fromEntries(values.map((value) => [value, value])) as {
      [TValue in TValues[number]]: TValue;
    },
  );

export const TagLayerValues = ["FUNCTIONAL", "TRIBAL", "THEME"] as const;
export const TagLayer = createStringEnum(TagLayerValues);

export const TagSourceValues = ["TAGGER", "RULE", "LLM", "HUMAN"] as const;
export const TagSource = createStringEnum(TagSourceValues);

export const TagStatusValues = ["ACTIVE", "DEPRECATED", "HIDDEN"] as const;
export const TagStatus = createStringEnum(TagStatusValues);

export const TagAliasMappingTypeValues = [
  "EXACT",
  "SYNONYM",
  "HEURISTIC",
  "MANUAL",
] as const;
export const TagAliasMappingType = createStringEnum(TagAliasMappingTypeValues);

export const UnresolvedTagCandidateStatusValues = [
  "OPEN",
  "MAPPED",
  "REJECTED",
] as const;
export const UnresolvedTagCandidateStatus = createStringEnum(
  UnresolvedTagCandidateStatusValues,
);

export const UnresolvedTagResolutionTypeValues = [
  "ADD_ALIAS",
  "ADD_CANONICAL_TAG",
  "REJECT_NOISE",
] as const;
export const UnresolvedTagResolutionType = createStringEnum(
  UnresolvedTagResolutionTypeValues,
);

export const CardProfileStatusValues = ["READY", "STALE", "ERROR"] as const;
export const CardProfileStatus = createStringEnum(CardProfileStatusValues);

export const TagAssertionStatusValues = [
  "ACCEPTED",
  "SHADOW",
  "NEEDS_REVIEW",
  "REJECTED",
] as const;
export const TagAssertionStatus = createStringEnum(TagAssertionStatusValues);

export const TagReviewStateValues = [
  "UNREVIEWED",
  "VERIFIED",
  "OVERRIDDEN",
] as const;
export const TagReviewState = createStringEnum(TagReviewStateValues);

export const TagConfidenceBandValues = ["HIGH", "MEDIUM", "LOW"] as const;
export const TagConfidenceBand = createStringEnum(TagConfidenceBandValues);

export const TagPrimaryScoringStatusValues = [
  "INCLUDED",
  "EXCLUDED_LOW_CONFIDENCE",
] as const;
export const TagPrimaryScoringStatus = createStringEnum(
  TagPrimaryScoringStatusValues,
);

export const TaxonomyArtifactKindValues = [
  "CANONICAL_TAGS",
  "TAG_ALIASES",
] as const;
export const TaxonomyArtifactKind = createStringEnum(
  TaxonomyArtifactKindValues,
);

export const TaxonomyRunTypeValues = [
  "CANONICAL_BOOTSTRAP",
  "ALIAS_BOOTSTRAP",
  "ALIAS_SYNC",
] as const;
export const TaxonomyRunType = createStringEnum(TaxonomyRunTypeValues);

export const TaxonomyWorkspaceIdValues = ["CANONICAL_BOOTSTRAP"] as const;
export const TaxonomyWorkspaceId = createStringEnum(TaxonomyWorkspaceIdValues);

export const TaxonomyRunStatusValues = [
  "PENDING",
  "SCANNING",
  "DETAILING",
  "PROPOSING",
  "AWAITING_REVIEW",
  "GENERATING_HIERARCHY",
  "PUBLISHING",
  "PUBLISH_FAILED",
  "COMPLETED",
  "FAILED",
] as const;
export const TaxonomyRunStatus = createStringEnum(TaxonomyRunStatusValues);

export const TaxonomyApprovalJobTypeValues = [
  "CANONICAL_PROPOSALS",
  "CANONICAL_HIERARCHY",
] as const;
export const TaxonomyApprovalJobType = createStringEnum(
  TaxonomyApprovalJobTypeValues,
);

export const TaxonomyApprovalJobStatusValues = [
  "QUEUED",
  "RUNNING",
  "COMPLETED",
  "FAILED",
] as const;
export const TaxonomyApprovalJobStatus = createStringEnum(
  TaxonomyApprovalJobStatusValues,
);

export const TaxonomyApprovalRequestItemStatusValues = [
  "PENDING",
  "APPROVED",
  "SKIPPED_INVALID",
  "FAILED",
] as const;
export const TaxonomyApprovalRequestItemStatus = createStringEnum(
  TaxonomyApprovalRequestItemStatusValues,
);

export const AsyncWorkflowTypeValues = [
  "TAXONOMY_SYNC_RUN",
  "USER_IMPORT",
] as const;
export const AsyncWorkflowType = createStringEnum(AsyncWorkflowTypeValues);

export const AsyncWorkflowWarningSeverityValues = [
  "WARNING",
  "ERROR_NON_FATAL",
] as const;
export const AsyncWorkflowWarningSeverity = createStringEnum(
  AsyncWorkflowWarningSeverityValues,
);

export const TaxonomyTriggerSourceValues = [
  "MANUAL",
  "SCHEDULED",
  "CONTINUATION",
] as const;
export const TaxonomyTriggerSource = createStringEnum(
  TaxonomyTriggerSourceValues,
);

export const CanonicalBootstrapModelIdValues = [
  "gpt-4.1-nano",
  "gpt-4o-mini",
  "gpt-5.4-nano",
  "gpt-5.4-mini",
  "gpt-5.4",
] as const;
export const CanonicalBootstrapModelId = createStringEnum(
  CanonicalBootstrapModelIdValues,
);

export const TaxonomyReviewItemTypeValues = [
  "CANONICAL_PROPOSAL",
  "CANONICAL_HIERARCHY_PROPOSAL",
  "TAG_ALIAS",
] as const;
export const TaxonomyReviewItemType = createStringEnum(
  TaxonomyReviewItemTypeValues,
);

export const TaxonomyReviewStatusValues = [
  "PENDING",
  "APPROVED",
  "REJECTED",
  "IGNORED",
  "PUBLISHED",
] as const;
export const TaxonomyReviewStatus = createStringEnum(
  TaxonomyReviewStatusValues,
);

export const CanonicalTagProposalStatusValues = [
  "PENDING",
  "APPROVED",
  "REJECTED",
] as const;
export const CanonicalTagProposalStatus = createStringEnum(
  CanonicalTagProposalStatusValues,
);

export const TagAliasDecisionStatusValues = [
  "PENDING",
  "AUTO_APPROVED",
  "APPROVED",
  "REJECTED",
  "IGNORED",
] as const;
export const TagAliasDecisionStatus = createStringEnum(
  TagAliasDecisionStatusValues,
);

export const TaxonomyProposalSourceValues = [
  "DETERMINISTIC",
  "LLM",
  "HUMAN",
] as const;
export const TaxonomyProposalSource = createStringEnum(
  TaxonomyProposalSourceValues,
);

export const SourceTagRelevanceValues = [
  "RELEVANT_CARD",
  "IGNORED_NON_CARD",
] as const;
export const SourceTagRelevance = createStringEnum(SourceTagRelevanceValues);

export const LlmProviderValues = ["OPENAI"] as const;
export const LlmProvider = createStringEnum(LlmProviderValues);

export const LlmRouteClassValues = [
  "SYNC_PRIMARY",
  "SYNC_FALLBACK",
  "ASYNC_BATCH",
] as const;
export const LlmRouteClass = createStringEnum(LlmRouteClassValues);

export const LlmUsageSubsystemValues = [
  "TAXONOMY",
  "SOURCE_INTEGRATION",
  "EVALUATION",
  "TEST",
] as const;
export const LlmUsageSubsystem = createStringEnum(LlmUsageSubsystemValues);

export const LlmUsageWorkloadValues = [
  "CANONICAL_BOOTSTRAP",
  "CANONICAL_HIERARCHY",
  "ALIAS_BOOTSTRAP",
  "ALIAS_SYNC",
  "SOURCE_INTEGRATION",
  "MODEL_EVALUATION",
  "INTEGRATION_TEST",
] as const;
export const LlmUsageWorkload = createStringEnum(LlmUsageWorkloadValues);

export const CanonicalHierarchyRunStatusValues = [
  "PENDING",
  "GENERATING",
  "COMPLETED",
  "FAILED",
] as const;
export const CanonicalHierarchyRunStatus = createStringEnum(
  CanonicalHierarchyRunStatusValues,
);

export const CanonicalHierarchyRecommendedActionValues = [
  "RUN_AGAIN",
  "PUBLISH_READY",
  "REVIEW_WARNINGS",
] as const;
export const CanonicalHierarchyRecommendedAction = createStringEnum(
  CanonicalHierarchyRecommendedActionValues,
);

export const CanonicalTagAssetValidationIssueCodeValues = [
  "DUPLICATE_CANONICAL_TAG",
  "DUPLICATE_PARENT_SLUG",
  "MISSING_SAME_LAYER_PARENT",
  "TOO_MANY_DIRECT_PARENTS",
  "CYCLE_DETECTED",
  "MAX_DEPTH_EXCEEDED",
] as const;
export const CanonicalTagAssetValidationIssueCode = createStringEnum(
  CanonicalTagAssetValidationIssueCodeValues,
);

export const CanonicalBootstrapValidationBlockerSourceTypeValues = [
  "APPROVED_CANONICAL_TAG",
  "APPROVED_SYNTHESIZED_HIERARCHY_PARENT",
] as const;
export const CanonicalBootstrapValidationBlockerSourceType = createStringEnum(
  CanonicalBootstrapValidationBlockerSourceTypeValues,
);

export const CanonicalDuplicateAnalysisTypeValues = [
  "CANONICAL_PROPOSALS",
  "HIERARCHY_SYNTHESIZED_PARENTS",
] as const;
export const CanonicalDuplicateAnalysisType = createStringEnum(
  CanonicalDuplicateAnalysisTypeValues,
);

export const CanonicalDuplicateSourceTypeValues = [
  "PENDING_CANONICAL_PROPOSAL",
  "APPROVED_CANONICAL_TAG",
  "PENDING_SYNTHESIZED_HIERARCHY_PARENT",
  "APPROVED_SYNTHESIZED_HIERARCHY_PARENT",
] as const;
export const CanonicalDuplicateSourceType = createStringEnum(
  CanonicalDuplicateSourceTypeValues,
);

export const CanonicalDuplicateFixActionTypeValues = [
  "APPROVE_PENDING_AND_REJECT_PENDING_DUPLICATES",
  "KEEP_APPROVED_AND_REJECT_PENDING_DUPLICATES",
] as const;
export const CanonicalDuplicateFixActionType = createStringEnum(
  CanonicalDuplicateFixActionTypeValues,
);

export const CanonicalHierarchyRerunReasonValues = [
  "SYNTHESIZED_NEW_PARENTS",
  "PARENTLESS_TAGS_REMAIN",
  "UNRESOLVED_SIBLING_GROUPS",
  "LOW_CONFIDENCE_LINKS",
  "VALIDATION_PRUNED_LINKS",
  "APPROVED_SET_CHANGED",
  "RERUN_CAP_REACHED",
] as const;
export const CanonicalHierarchyRerunReason = createStringEnum(
  CanonicalHierarchyRerunReasonValues,
);

export const LlmUsageEntityTypeValues = [
  "TAG_CLUSTER",
  "SOURCE_TAG",
  "CARD",
  "RUN",
  "TEST_CASE",
] as const;
export const LlmUsageEntityType = createStringEnum(LlmUsageEntityTypeValues);

export const LlmUsageEventStatusValues = ["COMPLETED", "FAILED"] as const;
export const LlmUsageEventStatus = createStringEnum(LlmUsageEventStatusValues);

export const LlmUsageFailureReasonValues = [
  "PROVIDER_REQUEST_FAILED",
  "PROVIDER_RESPONSE_INVALID",
  "USAGE_UNAVAILABLE",
  "STRUCTURED_OUTPUT_MISSING",
  "STRUCTURED_OUTPUT_INVALID_JSON",
  "UNKNOWN",
] as const;
export const LlmUsageFailureReason = createStringEnum(
  LlmUsageFailureReasonValues,
);

export const UserImportSourceTypeValues = ["UPLOAD", "URL"] as const;
export const UserImportSourceType = createStringEnum(
  UserImportSourceTypeValues,
);

export const UserImportStatusValues = [
  "AWAITING_UPLOAD",
  "QUEUED",
  "PROCESSING",
  "IN_PROGRESS",
  "COMMITTED",
  "FAILED",
] as const;
export const UserImportStatus = createStringEnum(UserImportStatusValues);

export const ActiveUserImportStatuses = [
  UserImportStatus.AWAITING_UPLOAD,
  UserImportStatus.QUEUED,
  UserImportStatus.PROCESSING,
  UserImportStatus.IN_PROGRESS,
] as const;

export const UserImportErrorPhaseValues = [
  "IMPORT",
  "QUEUE",
  "FETCH",
  "WRITE",
] as const;
export const UserImportErrorPhase = createStringEnum(
  UserImportErrorPhaseValues,
);

export const UserImportErrorCodeValues = [
  "IMPORT_STALE_TIMEOUT",
  "QUEUE_FAILED",
  "SOURCE_READ_FAILED",
  "FETCH_FAILED_SCRYFALL",
  "FETCH_FAILED_TAGGER",
  "FETCH_FAILED_MIXED",
  "WRITE_FAILED_DYNAMO",
] as const;
export const UserImportErrorCode = createStringEnum(UserImportErrorCodeValues);

export const UserImportCardStatusStageValues = ["FETCH", "WRITE"] as const;
export const UserImportCardStatusStage = createStringEnum(
  UserImportCardStatusStageValues,
);

export const UserImportCardStatusValueValues = [
  "PENDING",
  "FETCH_FAILED_SCRYFALL",
  "FETCH_FAILED_TAGGER",
  "READY_TO_WRITE",
  "WRITE_FAILED",
  "WRITTEN",
] as const;
export const UserImportCardStatusValue = createStringEnum(
  UserImportCardStatusValueValues,
);

const userImportCardStatusValueSet = new Set<string>(
  UserImportCardStatusValueValues,
);

export const isUserImportCardStatusValue = (
  value: unknown,
): value is (typeof UserImportCardStatusValueValues)[number] =>
  typeof value === "string" && userImportCardStatusValueSet.has(value);
